import React from "react";
import {
  TextField,
  Box,
  Typography,
  Autocomplete,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BondAttorneyQuickAddDialog from "../../../../features/bonds/shared/components/BondAttorneyQuickAddDialog";
import BondQuickAddLawFirmDialog from "../../../../features/bonds/shared/components/BondQuickAddLawFirmDialog";
import { LawFirmFuzzyMatchDialogBound } from "../../../../features/bonds/shared/components/LawFirmFuzzyMatchDialog";
import { useRetrievalAttorneyInformation } from "../hooks/useRetrievalAttorneyInformation";

const RetrievalAttorneyInformationBase = ({
  cfg,
  globalData,
  data,
  updateData,
  role,
  isDisabled,
}) => {
  const vm = useRetrievalAttorneyInformation({ cfg, data, updateData });
  const {
    navigate,
    attorneyInvolved,
    setAttorneyInvolved,
    lawFirms,
    lawFirmLocations,
    attorneys,
    loadingLawFirms,
    loadingLocations,
    loadingAttorneys,
    openAttorneyDialog,
    openLawFirmDialog,
    openAddressDialog,
    newAttorney,
    setNewAttorney,
    newLawFirm,
    setNewLawFirm,
    newAddress,
    setNewAddress,
    fuzzyMatchDialogOpen,
    setFuzzyMatchDialogOpen,
    fuzzyMatchResult,
    setFuzzyMatchResult,
    handleLawFirmChange,
    handleOpenAttorneyDialog,
    handleCloseAttorneyDialog,
    handleOpenLawFirmDialog,
    handleCloseLawFirmDialog,
    handleOpenAddressDialog,
    handleCloseAddressDialog,
    handleAddAttorney,
    handleAddLawFirm,
    handleUseExistingLawFirm,
    handleCreateNewLawFirmAnyway,
    handleAddAddress,
    selectedLocation,
  } = vm;

  const showForm = attorneyInvolved === "Yes";

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Attorney Information
      </Typography>

      {cfg.showAttorneyInvolvedToggle && (
        <FormControl disabled={isDisabled} component="fieldset" sx={{ mb: 4 }}>
          <Typography variant="body1">
            Will an attorney remain involved throughout the duration of the case?
          </Typography>
          <RadioGroup
            row
            name="attorneyInvolved"
            value={attorneyInvolved}
            onChange={(e) => setAttorneyInvolved(e.target.value)}
          >
            <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="No" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      )}

      {showForm && (
        <Box className="grid grid-cols-1 gap-4">
          <Autocomplete
            options={lawFirms}
            disabled={isDisabled}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingLawFirms}
            value={lawFirms.find((firm) => firm.value === data.lawFirm) || null}
            onChange={handleLawFirmChange}
            renderInput={(params) => (
              <TextField
                disabled={isDisabled}
                {...params}
                label="Law Firm"
                variant="outlined"
                fullWidth
                slotProps={{ input: { ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLawFirms ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}}
              />
            )}
          />
          {globalData?.attorneyInformation?.is_guest && data.guest_lawfirm_name && (
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Guest Law Firm: {data.guest_lawfirm_name}
            </Typography>
          )}
          <Button
            disabled={isDisabled}
            variant="outlined"
            onClick={handleOpenLawFirmDialog}
            sx={{ mb: 2 }}
          >
            {loadingLawFirms ? <CircularProgress size={24} /> : "Add New Law Firm"}
          </Button>

          <Autocomplete
            options={lawFirmLocations}
            disabled={isDisabled}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingLocations}
            value={
              lawFirmLocations.find((loc) => loc.value === data.lawFirmAddress) || null
            }
            onChange={(event, newValue) => {
              updateData({
                ...data,
                lawFirmAddress: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <TextField
                disabled={isDisabled}
                {...params}
                label="Law Firm Address"
                variant="outlined"
                fullWidth
                slotProps={{ input: { ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLocations ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}}
              />
            )}
          />
          {globalData?.attorneyInformation?.is_guest && data.guest_lawfirmaddress_name && (
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Guest Address: {data.guest_lawfirmaddress_name}
            </Typography>
          )}

          {(role === "internal_user" || role === "super_user") &&
            selectedLocation?.customer_number === null &&
            !isDisabled && (
              <Box
                sx={{
                  width: "100%",
                  mb: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <Box sx={{ color: "warning.main", mt: 0.25 }}>
                  <WarningAmberIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Location customer number
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    The location does not have a customer number. Please update it from
                    Account Management (Law Firms). Otherwise system will automatically
                    create a new law firm with a customer number in AMS.
                  </Typography>
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        navigate(`/lawfirmlocationdetails/${data.lawFirmAddress}`)
                      }
                    >
                      Update Now
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

          <Button
            disabled={isDisabled}
            variant="outlined"
            onClick={handleOpenAddressDialog}
            sx={{ mb: 2 }}
          >
            {loadingLocations ? <CircularProgress size={24} /> : "Add New Address"}
          </Button>

          <Autocomplete
            options={attorneys}
            disabled={isDisabled}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingAttorneys}
            value={
              attorneys.find((attorney) => attorney.value === data.attorneyOfRecord) ||
              null
            }
            onChange={(event, newValue) => {
              updateData({
                ...data,
                attorneyOfRecord: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <TextField
                disabled={isDisabled}
                {...params}
                label="Attorney of Record"
                variant="outlined"
                fullWidth
                slotProps={{ input: { ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAttorneys ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}}
              />
            )}
          />
          {globalData?.attorneyInformation?.is_guest && data.guest_attorney_name && (
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Guest Attorney: {data.guest_attorney_name}
            </Typography>
          )}
          {data?.is_guest && (data?.referral_source || data?.referred_by) && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                backgroundColor: "#f0f4ff",
                borderRadius: 1,
                border: "1px solid #d0d9f0",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1a237e", mb: 0.5 }}>
                Referral Information
              </Typography>
              {data.referral_source && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  How did you find us: {data.referral_source}
                </Typography>
              )}
              {data.referred_by && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Referred by: {data.referred_by}
                </Typography>
              )}
            </Box>
          )}
          <Button
            disabled={isDisabled}
            variant="outlined"
            onClick={handleOpenAttorneyDialog}
            sx={{ mb: 2 }}
          >
            {loadingAttorneys ? <CircularProgress size={24} /> : "Add New Attorney"}
          </Button>
        </Box>
      )}

      <BondAttorneyQuickAddDialog
        open={openAttorneyDialog}
        onClose={handleCloseAttorneyDialog}
        onSubmit={handleAddAttorney}
        attorney={newAttorney}
        setAttorney={setNewAttorney}
        disabled={isDisabled}
      />

      <BondQuickAddLawFirmDialog
        open={openLawFirmDialog}
        onClose={handleCloseLawFirmDialog}
        onSubmit={handleAddLawFirm}
        lawFirm={newLawFirm}
        setLawFirm={setNewLawFirm}
        disabled={isDisabled}
      />

      <LawFirmFuzzyMatchDialogBound
        open={fuzzyMatchDialogOpen}
        setOpen={setFuzzyMatchDialogOpen}
        fuzzyMatchResult={fuzzyMatchResult}
        setFuzzyMatchResult={setFuzzyMatchResult}
        onUseExisting={handleUseExistingLawFirm}
        onCreateAnyway={handleCreateNewLawFirmAnyway}
      />

      <Dialog open={openAddressDialog} onClose={handleCloseAddressDialog} fullWidth>
        <DialogTitle>Add New Address</DialogTitle>
        <DialogContent>
          {["address", "zip", "city", "county", "state", "phone"].map((field) => (
            <TextField
              key={field}
              disabled={isDisabled}
              label={field === "zip" ? "ZIP" : field.charAt(0).toUpperCase() + field.slice(1)}
              value={newAddress[field]}
              onChange={(e) => setNewAddress({ ...newAddress, [field]: e.target.value })}
              fullWidth
              required
              margin="normal"
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddressDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddAddress} color="primary">
            Add Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RetrievalAttorneyInformationBase;
